// Copyright 2025, University of Maryland, All Rights Reserved
import type { Config } from '@/api/types/config'
import type { JSONSchema } from '@/api/types/schema'
import { isValidSchemaType } from '@/types/schema-registry'
import type { SchemaFieldType, SchemaFieldConfig } from '@/types/schema-fields'

/**
 * Gets the schema for a specific schema type from config
 */
export function getSchemaForType(config: Config | undefined, schema_type: string): JSONSchema | undefined {
  if (!config?.prompt_config || !isValidSchemaType(schema_type)) {
    return undefined
  }
  
  const prompt_config = config.prompt_config[schema_type]
  if (!prompt_config?.json_schema) {
    return undefined
  }
  
  return prompt_config.json_schema as unknown as JSONSchema
}

/**
 * Updates the schema for a specific schema type in config
 */
export function updateSchemaForType(
  config: Config | undefined, 
  schema_type: string, 
  schema: JSONSchema
): Partial<Config> {
  if (!isValidSchemaType(schema_type)) {
    throw new Error(`Invalid schema type: ${schema_type}`)
  }
  
  const current_prompt_config = config?.prompt_config?.[schema_type]
  
  return {
    prompt_config: {
      ...config?.prompt_config,
      [schema_type]: {
        prompt_text: current_prompt_config?.prompt_text || '',
        model_name: current_prompt_config?.model_name || 'bedrock_claude_45_sonnet',
        json_schema: schema as unknown as Record<string, unknown>
      }
    }
  }
}

/**
 * Gets the default schema type from config, falls back to 'monitor'
 */
export function getDefaultSchemaType(config: Config | undefined): string {
  if (!config?.prompt_config) {
    return 'monitor'
  }
  
  // Get available schema types from the config
  const available_types = Object.keys(config.prompt_config).filter(type => 
    isValidSchemaType(type) && config.prompt_config![type]?.json_schema
  )
  
  // If we have available types, return the first one (or 'monitor' if it exists)
  if (available_types.length > 0) {
    return available_types.includes('monitor') ? 'monitor' : available_types[0]
  }
  
  // Fallback to 'monitor' if no types are available
  return 'monitor'
}


/**
 * Get available field types for a schema type
 */
export function getAvailableFieldTypes(schema_type: string): SchemaFieldType[] {
  switch (schema_type) {
    case 'monitor':
      return ['integer', 'enum_field', 'string', 'score']
    case 'legislative_review':
      return ['string', 'simple_array', 'object_array', 'integer', 'enum_field', 'score']
    default:
      return ['integer', 'enum_field', 'string'] // default to monitor types
  }
}

/**
 * Get default configuration for each field type
 */
export function getFieldTypeConfig(field_type: SchemaFieldType): SchemaFieldConfig {
  switch (field_type) {
    case 'string':
      return {
        type: 'string',
        title: 'Text Field',
        description: 'Simple text field for AI-generated content',
        required: true
      }
    case 'integer':
      return {
        type: 'integer',
        title: 'Number Field',
        description: 'Numeric field',
        min: 1,
        max: 10,
        required: true
      }
    case 'simple_array':
      return {
        type: 'simple_array',
        title: 'Text List',
        description: 'Array of text items',
        required: true
      }
    case 'object_array':
      return {
        type: 'object_array',
        title: 'Object List',
        description: 'Array of structured objects with defined properties',
        required: true,
        properties: {}
      }
    case 'enum_field':
      return {
        type: 'enum_field',
        title: 'Selection Field',
        description: 'Selection from predefined options',
        required: true,
        enum_options: ['positive', 'negative', 'neutral']
      }
    case 'score':
      return {
        type: 'score',
        title: 'Score Field',
        description: 'Combined scoring field with numeric score and score_type classification',
        required: true,
        score_min: 0,
        score_max: 10,
        type_options: ['positive', 'neutral', 'negative']
      }
    default:
      return {
        type: 'string',
        title: 'Unknown Field',
        description: 'Unknown field type',
        required: false
      }
  }
}

/**
 * Check if a field type is compatible with a schema type
 */
export function isFieldTypeCompatible(schema_type: string, field_type: SchemaFieldType): boolean {
  const available_types = getAvailableFieldTypes(schema_type)
  return available_types.includes(field_type)
}
